import React, { createContext, useContext, useEffect, useState } from 'react';
import api, { tokenStorage } from '../config/api';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    checkAuthState();
  }, []);

  const checkAuthState = async () => {
    try {
      const token = await tokenStorage.getToken();
      const userData = await tokenStorage.getUserData();
      
      if (token && userData) {
        setUser(userData);
      }
    } catch (error) {
      console.error('Error checking auth state:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (email, password) => {
    try {
      const response = await api.post('/auth/login', {
        email,
        password,
      });

      if (response.data.success) {
        const { user: userData, token } = response.data.data;
        
        // Verify user is a driver
        if (userData.role !== 'DRIVER') {
          return { success: false, message: 'Access denied. Driver account required.' };
        }
        
        // Store token and user data securely
        await tokenStorage.setToken(token);
        await tokenStorage.setUserData(userData);
        
        setUser(userData);
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Login error:', error);
      const message = error.response?.data?.message || 'Login failed';
      return { success: false, message };
    }
  };

  const register = async (name, email, password) => {
    try {
      const response = await api.post('/auth/register', {
        name,
        email,
        password,
        role: 'DRIVER', // Set role as DRIVER for driver app
      });

      if (response.data.success) {
        const { user: userData, token } = response.data.data;
        
        // Store token and user data securely
        await tokenStorage.setToken(token);
        await tokenStorage.setUserData(userData);
        
        setUser(userData);
        return { success: true };
      } else {
        return { success: false, message: response.data.message };
      }
    } catch (error) {
      console.error('Registration error:', error);
      const message = error.response?.data?.message || 'Registration failed';
      return { success: false, message };
    }
  };

  const logout = async () => {
    try {
      // Clear stored data
      await tokenStorage.removeToken();
      await tokenStorage.removeUserData();
      
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const value = {
    user,
    isLoading,
    login,
    register,
    logout,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
